import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { useCampaign } from "@/features/campaigns/api/useCampaign";
import { useCampaignJobBoard } from "@/features/campaigns/jobboards/api/useCampaignJobBoard";
import { useEditCampaignJobBoardMutation } from "@/features/campaigns/jobboards/api/useEditCampaignJobBoardMutation";
import EditCampaignJobBoardForm from "@/features/campaigns/jobboards/components/CampaignJobBoardForm/EditCampaignJobBoardForm";
import {
  getCampaignDetailPath,
  getCampaignIntegrationDetailPath,
  getCampaignsPath,
} from "@/features/campaigns/utils/navigation";
import { translateIntegrationDisplayName } from "@/features/integrations/utils";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import useToast from "@/lib/hooks/useToast";
import { EditCampaignJobBoardRequest } from "@/types/request/EditCampaignJobBoardRequest";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

function EditCampaignJobBoardPage() {
  const { campaignId, clientId, clientIntegrationModuleId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resolvedClientId = clientId ?? "";
  const resolvedCampaignId = campaignId ?? "";
  const resolvedClientIntegrationModuleId = clientIntegrationModuleId ?? "";

  const campaignsPath = getCampaignsPath(clientId);
  const campaignDetailPath = getCampaignDetailPath(
    clientId,
    resolvedCampaignId
  );
  const integrationDetailPath = getCampaignIntegrationDetailPath(
    clientId,
    resolvedCampaignId,
    resolvedClientIntegrationModuleId
  );

  const handleNavigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const { data: campaign, isLoading: isCampaignLoading } = useCampaign({
    campaignId: resolvedCampaignId,
    clientId: resolvedClientId || undefined,
  });

  const { data: campaignJobBoard, isLoading: isJobBoardLoading } =
    useCampaignJobBoard({
      campaignId: resolvedCampaignId,
      clientIntegrationModuleId: resolvedClientIntegrationModuleId,
      clientId: resolvedClientId,
    });

  const editCampaignJobBoard = useEditCampaignJobBoardMutation({
    campaignId: resolvedCampaignId,
    clientId: resolvedClientId,
    clientIntegrationModuleId: resolvedClientIntegrationModuleId,
  });

  useEffect(() => {
    if (campaignJobBoard?.endDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = new Date(campaignJobBoard.endDate);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < today) {
        navigate(-1);
      }
    }
  }, [campaignJobBoard, navigate]);

  const handleSubmit = useCallback(
    (payload: EditCampaignJobBoardRequest) => {
      setErrorMessage(null);
      editCampaignJobBoard.mutate(payload, {
        onSuccess: () => {
          showToast(t("success.updated", { entity: t("common.jobBoard") }));
          handleNavigateBack();
        },
        onError: (error: AxiosError<ValidationErrorResponse>) => {
          let message: string;

          if (Array.isArray(error.response?.data)) {
            message = error.response.data
              .map(({ errorMessage }) => t(`validation.${errorMessage}`))
              .at(0) ?? t("errors.somethingWentWrong");
          } else if (error.response?.data && typeof error.response.data === "object" && "detail" in error.response.data) {
            message = (error.response.data as { detail: string }).detail;
          } else {
            message = t("errors.somethingWentWrong");
          }

          setErrorMessage(message);
          showToast(message, "error");
        },
      });
    },
    [editCampaignJobBoard, handleNavigateBack, showToast, t]
  );

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.campaigns"), href: campaignsPath },
      {
        label: campaign?.name ?? t("common.campaign"),
        href: campaignDetailPath,
      },
      { label: t("common.jobBoards") },
      {
        label: translateIntegrationDisplayName(campaignJobBoard?.integrationDisplayName ?? campaignJobBoard?.jobBoardName, t, i18n) || t("common.jobBoard"),
        href: integrationDetailPath,
      },
      { label: t("common.edit") },
    ],
  });

  const isLoading = isCampaignLoading || isJobBoardLoading;
  const hasRequiredParams = Boolean(campaignId && clientIntegrationModuleId);

  if (!hasRequiredParams) {
    return null;
  }

  let content: ReactNode;

  if (isLoading) {
    content = (
      <div className="flex h-full w-full items-center justify-center p-6">
        <Loader2 className="size-16 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (!campaignJobBoard) {
    content = (
      <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
        {t("errors.jobBoardNotFound", {
          defaultValue: t("errors.somethingWentWrong"),
        })}
      </div>
    );
  } else {
    content = (
      <EditCampaignJobBoardForm
        campaignId={resolvedCampaignId}
        campaignJobBoard={campaignJobBoard}
        clientId={resolvedClientId}
        errorMessage={errorMessage}
        clientIntegrationModuleId={resolvedClientIntegrationModuleId}
        isSubmitting={editCampaignJobBoard.isPending}
        onSubmitForm={handleSubmit}
        onSuccess={handleNavigateBack}
      />
    );
  }

  return (
    <ModernPageLayout
      backbutton={{
        href: "../",
        label: t("common.back"),
      }}
      breadcrumbs={breadcrumbs}
      title={t("campaignJobBoard.pages.edit.title", {
        jobBoardName:
          (translateIntegrationDisplayName(
            campaignJobBoard?.integrationDisplayName ?? campaignJobBoard?.jobBoardName,
            t,
            i18n
          ) || (campaignJobBoard?.integrationDisplayName ?? campaignJobBoard?.jobBoardName)) ?? "",
      })}
      subtitle={t("campaignJobBoard.pages.edit.subtitle", {
        campaignName: campaign?.name,
      })}
    >
      {content}
    </ModernPageLayout>
  );
}

export default EditCampaignJobBoardPage;
